import { ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { formatEther } from 'ethers/lib/utils';

import { Table } from '../Table';
import { useLiveRecentTradesSubscription, Price } from '../../graphql/generated';
import { TimeStamp } from '../Text';
import useMarkets from '../../contexts/MarketsContext';

interface Row {
  id: string;
  price: ReactNode;
  size: ReactNode;
  time: ReactNode;
}

const ValueWrapper = styled.span<{ value: number }>`
  color: ${({ value, theme }) => (value >= 0 ? theme.green : theme.red)};
`;

const RightAlignWrapper = styled.div`
  margin: 0.25rem 0;
  padding: 0;
  width: 100%;
  text-align: right;
`;

const SizeWrapper = styled(RightAlignWrapper)`
  padding-right: 50%;
  width: 80%;
`;

const TableContainer = styled.div`
  max-height: 100%;
  overflow: scroll;
`;

const headers = (base: string, t: TFunction): Row => ({
  id: 'header',
  price: t('Price'),
  size: (
    <SizeWrapper>
      {t('Size')} ({base})
    </SizeWrapper>
  ),
  time: <RightAlignWrapper>{t('Time')} (UTC)</RightAlignWrapper>,
});

const renderRow = ({
  id,
  timestamp,
  value,
  baseVal,
  displayDecimals,
  baseDecimals,
}: { displayDecimals: number; baseDecimals: number } & Price): Row => {
  const displayPrice = Number(formatEther(value)).toFixed(displayDecimals);
  const displayValue = Math.abs(Number(formatEther(baseVal))).toFixed(baseDecimals);
  const displayTime = new Date(Number(timestamp * 1000)).toISOString().substring(11, 19);

  return {
    id,
    price: displayPrice,
    size: (
      <SizeWrapper>
        <ValueWrapper value={baseVal}>{displayValue}</ValueWrapper>
      </SizeWrapper>
    ),
    time: (
      <RightAlignWrapper>
        <TimeStamp>{displayTime}</TimeStamp>
      </RightAlignWrapper>
    ),
  };
};

export function MarketHistory() {
  const { selectedMarket } = useMarkets();
  const { t } = useTranslation();
  const [result] = useLiveRecentTradesSubscription({
    variables: { marketId: selectedMarket?.id ?? '0', from: 0, numTrades: 500 },
  });

  const renderedData = useMemo(() => {
    const rows = result?.data?.market?.prices as Price[] | undefined;
    const marketId = result?.data?.market?.id;
    if (rows && marketId !== undefined && selectedMarket && marketId === selectedMarket.id) {
      return rows
        .filter(({ baseVal }) => baseVal !== '0')
        .map((row) =>
          renderRow({
            ...row,
            baseDecimals: selectedMarket?.baseDecimals ?? 5,
            displayDecimals: selectedMarket?.displayDecimals ?? 5,
          }),
        );
    }
    return null;
  }, [result, selectedMarket]);

  return (
    <TableContainer>
      <Table headerData={headers(selectedMarket?.base ?? '', t)} data={renderedData} />
    </TableContainer>
  );
}
